import config from '../config';
import { cordovaApiBaseUrl, stripeSetupIntent, stripeUpdateCustomer, stripeUpdatePaymentIntent } from '../util/api';
import { storableError } from '../util/errors';
import { logError } from '../util/log';
import { isCordova } from '../util/mobile/deviceDetection';
import { fetchCurrentUser } from './user.duck';

// ================ Action types ================ //

export const STRIPE_ACCOUNT_CLEAR_ERROR = 'app/stripe/STRIPE_ACCOUNT_CLEAR_ERROR';

export const ACCOUNT_OPENER_CREATE_REQUEST = 'app/stripe/ACCOUNT_OPENER_CREATE_REQUEST';
export const ACCOUNT_OPENER_CREATE_SUCCESS = 'app/stripe/ACCOUNT_OPENER_CREATE_SUCCESS';
export const ACCOUNT_OPENER_CREATE_ERROR = 'app/stripe/ACCOUNT_OPENER_CREATE_ERROR';

export const PERSON_CREATE_REQUEST = 'app/stripe/PERSON_CREATE_REQUEST';
export const PERSON_CREATE_SUCCESS = 'app/stripe/PERSON_CREATE_SUCCESS';
export const PERSON_CREATE_ERROR = 'app/stripe/PERSON_CREATE_ERROR';

export const HANDLE_STRIPE_PAYMENT_REQUEST = 'app/stripe/HANDLE_STRIPE_PAYMENT_REQUEST';
export const HANDLE_STRIPE_PAYMENT_SUCCESS = 'app/stripe/HANDLE_STRIPE_PAYMENT_SUCCESS';
export const HANDLE_STRIPE_PAYMENT_ERROR = 'app/stripe/HANDLE_STRIPE_PAYMENT_ERROR';

export const CREATE_SETUP_INTENT_REQUEST = 'app/stripe/CREATE_SETUP_INTENT_REQUEST';
export const CREATE_SETUP_INTENT_SUCCESS = 'app/stripe/CREATE_SETUP_INTENT_SUCCESS';
export const CREATE_SETUP_INTENT_ERROR = 'app/stripe/CREATE_SETUP_INTENT_ERROR';

export const HANDLE_STRIPE_SETUP_REQUEST = 'app/stripe/HANDLE_CARD_SETUP_REQUEST';
export const HANDLE_STRIPE_SETUP_SUCCESS = 'app/stripe/HANDLE_CARD_SETUP_SUCCESS';
export const HANDLE_STRIPE_SETUP_ERROR = 'app/stripe/HANDLE_CARD_SETUP_ERROR';

export const CLEAR_HANDLE_PAYMENT = 'app/stripe/CLEAR_HANDLE_PAYMENT';

export const RETRIEVE_PAYMENT_INTENT_REQUEST = 'app/stripe/RETRIEVE_PAYMENT_INTENT_REQUEST';
export const RETRIEVE_PAYMENT_INTENT_SUCCESS = 'app/stripe/RETRIEVE_PAYMENT_INTENT_SUCCESS';
export const RETRIEVE_PAYMENT_INTENT_ERROR = 'app/stripe/RETRIEVE_PAYMENT_INTENT_ERROR';

export const UPDATE_PAYMENT_INTENT_REQUEST = 'app/stripe/UPDATE_PAYMENT_INTENT_REQUEST';
export const UPDATE_PAYMENT_INTENT_SUCCESS = 'app/stripe/UPDATE_PAYMENT_INTENT_SUCCESS';
export const UPDATE_PAYMENT_INTENT_ERROR = 'app/stripe/UPDATE_PAYMENT_INTENT_ERROR';

export const UPDATE_STRIPE_CUSTOMER_REQUEST = 'app/stripe/UPDATE_STRIPE_CUSTOMER_REQUEST';
export const UPDATE_STRIPE_CUSTOMER_SUCCESS = 'app/stripe/UPDATE_STRIPE_CUSTOMER_SUCCESS';
export const UPDATE_STRIPE_CUSTOMER_ERROR = 'app/stripe/UPDATE_STRIPE_CUSTOMER_ERROR';

// ================ Reducer ================ //

/** @type {import('../../types/vansite').VanSite.State.Stripe} */
const initialState = {
  persons: [],
  confirmPaymentMethodInProgress: false,
  confirmPaymentMethodError: null,
  handlePaymentMethodSetupInProgress: false,
  handlePaymentMethodSetupError: null,
  paymentIntent: null,
  setupIntent: null,
  retrievePaymentIntentInProgress: false,
  retrievePaymentIntentError: null,
  setupIntentInProgress: false,
  setupIntentError: null,
  updatePaymentIntentInProgress: false,
  updatePaymentIntentError: null,
  updateStripeCustomerInProgress: false,
  updateStripeCustomerError: null,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case STRIPE_ACCOUNT_CLEAR_ERROR:
      return { ...initialState };

    case ACCOUNT_OPENER_CREATE_REQUEST:
      return {
        ...state,
        createAccountOpenerError: null,
        createAccountOpenerInProgress: true,
      };
    case ACCOUNT_OPENER_CREATE_SUCCESS:
      return { ...state, createAccountOpenerInProgress: false, personAccountOpener: payload };
    case ACCOUNT_OPENER_CREATE_ERROR:
      return { ...state, createAccountOpenerError: payload, createAccountOpenerInProgress: false };

    case PERSON_CREATE_REQUEST:
      return {
        ...state,
        persons: [
          ...state.persons,
          {
            ...payload,
            createStripePersonError: null,
            createStripePersonInProgress: true,
          },
        ],
      };
    case PERSON_CREATE_SUCCESS:
      return {
        ...state,
        persons: state.persons.map((p) => {
          return p.personToken === payload.personToken
            ? { ...payload, createStripePersonInProgress: false }
            : p;
        }),
      };
    case PERSON_CREATE_ERROR:
      return {
        ...state,
        persons: state.persons.map((p) => {
          return p.personToken === payload.personToken
            ? { ...p, createStripePersonInProgress: false, createStripePersonError: payload.error }
            : p;
        }),
      };

    case HANDLE_STRIPE_PAYMENT_REQUEST:
      return {
        ...state,
        confirmPaymentMethodError: null,
        confirmPaymentMethodInProgress: true,
      };
    case HANDLE_STRIPE_PAYMENT_SUCCESS:
      return { ...state, paymentIntent: payload, confirmPaymentMethodInProgress: false };
    case HANDLE_STRIPE_PAYMENT_ERROR:
      return {
        ...state,
        paymentIntent: payload?.payment_intent || null,
        confirmPaymentMethodError: payload,
        confirmPaymentMethodInProgress: false,
      };

    case CREATE_SETUP_INTENT_REQUEST:
      return { ...state, setupIntentInProgress: true, setupIntentError: null };
    case CREATE_SETUP_INTENT_SUCCESS:
      return {
        ...state,
        setupIntentInProgress: false,
        setupIntentError: null,
        setupIntent: payload,
      };
    case CREATE_SETUP_INTENT_ERROR:
      return { ...state, setupIntentInProgress: false, setupIntentError: null };

    case HANDLE_STRIPE_SETUP_REQUEST:
      return {
        ...state,
        handlePaymentMethodSetupError: null,
        handlePaymentMethodSetupInProgress: true,
      };
    case HANDLE_STRIPE_SETUP_SUCCESS:
      return { ...state, setupIntent: payload, handlePaymentMethodSetupInProgress: false };
    case HANDLE_STRIPE_SETUP_ERROR:
      return {
        ...state,
        handlePaymentMethodSetupError: payload,
        handlePaymentMethodSetupInProgress: false,
      };

    case CLEAR_HANDLE_PAYMENT:
      return {
        ...state,
        confirmPaymentMethodInProgress: false,
        confirmPaymentMethodError: null,
        paymentIntent: null,
      };

    case RETRIEVE_PAYMENT_INTENT_REQUEST:
      return {
        ...state,
        retrievePaymentIntentError: null,
        retrievePaymentIntentInProgress: true,
      };
    case RETRIEVE_PAYMENT_INTENT_SUCCESS:
      return { ...state, paymentIntent: payload, retrievePaymentIntentInProgress: false };
    case RETRIEVE_PAYMENT_INTENT_ERROR:
      return {
        ...state,
        retrievePaymentIntentError: payload,
        retrievePaymentIntentInProgress: false,
      };

    case UPDATE_PAYMENT_INTENT_REQUEST:
      return {
        ...state,
        updatePaymentIntentError: null,
        updatePaymentIntentInProgress: true,
      };
    case UPDATE_PAYMENT_INTENT_SUCCESS:
      return { ...state, paymentIntent: payload, updatePaymentIntentInProgress: false };
    case UPDATE_PAYMENT_INTENT_ERROR:
      return {
        ...state,
        updatePaymentIntentError: payload,
        updatePaymentIntentInProgress: false,
      };

    case UPDATE_STRIPE_CUSTOMER_REQUEST:
      return { ...state, updateStripeCustomerInProgress: true, updateStripeCustomerError: null };
    case UPDATE_STRIPE_CUSTOMER_SUCCESS:
      return { ...state, updateStripeCustomerInProgress: false, updateStripeCustomerError: null };
    case UPDATE_STRIPE_CUSTOMER_ERROR:
      return { ...state, updateStripeCustomerInProgress: false, updateStripeCustomerError: payload };

    default:
      return state;
  }
}

// ================ Action creators ================ //

export const stripeAccountClearError = () => ({
  type: STRIPE_ACCOUNT_CLEAR_ERROR,
});

export const confirmPaymentRequest = () => ({
  type: HANDLE_STRIPE_PAYMENT_REQUEST,
});

export const confirmStripePaymentSuccess = (payload) => ({
  type: HANDLE_STRIPE_PAYMENT_SUCCESS,
  payload,
});

export const confirmStripePaymentError = (payload) => ({
  type: HANDLE_STRIPE_PAYMENT_ERROR,
  payload,
  error: true,
});

export const handleStripeSetupRequest = () => ({
  type: HANDLE_STRIPE_SETUP_REQUEST,
});

export const handleStripeSetupSuccess = (payload) => ({
  type: HANDLE_STRIPE_SETUP_SUCCESS,
  payload,
});

export const handleStripeSetupError = (payload) => ({
  type: HANDLE_STRIPE_SETUP_ERROR,
  payload,
  error: true,
});

export const initializePaymentData = () => ({
  type: CLEAR_HANDLE_PAYMENT,
});

export const retrievePaymentIntentRequest = () => ({
  type: RETRIEVE_PAYMENT_INTENT_REQUEST,
});

export const retrievePaymentIntentSuccess = (payload) => ({
  type: RETRIEVE_PAYMENT_INTENT_SUCCESS,
  payload,
});

export const retrievePaymentIntentError = (payload) => ({
  type: RETRIEVE_PAYMENT_INTENT_ERROR,
  payload,
  error: true,
});

export const updatePaymentIntentRequest = () => ({
  type: UPDATE_PAYMENT_INTENT_REQUEST,
});

export const updatePaymentIntentSuccess = (payload) => ({
  type: UPDATE_PAYMENT_INTENT_SUCCESS,
  payload,
});

export const updatePaymentIntentError = (payload) => ({
  type: UPDATE_PAYMENT_INTENT_ERROR,
  payload,
  error: true,
});

export const setupIntentRequest = () => ({ type: CREATE_SETUP_INTENT_REQUEST });
export const setupIntentSuccess = (setupIntent) => ({
  type: CREATE_SETUP_INTENT_SUCCESS,
  payload: setupIntent,
});
export const setupIntentError = (e) => ({
  type: CREATE_SETUP_INTENT_ERROR,
  error: true,
  payload: e,
});

export const updateStripeCustomerRequest = () => ({ type: UPDATE_STRIPE_CUSTOMER_REQUEST });
export const updateStripeCustomerSuccess = () => ({ type: UPDATE_STRIPE_CUSTOMER_SUCCESS });
export const updateStripeCustomerError = (e) => ({ type: UPDATE_STRIPE_CUSTOMER_ERROR, error: true, payload: e });

// ================ Thunks ================ //

export const retrievePaymentIntent = (params) => (dispatch) => {
  const { stripe, stripePaymentIntentClientSecret } = params;
  dispatch(retrievePaymentIntentRequest());

  return stripe
    .retrievePaymentIntent(stripePaymentIntentClientSecret)
    .then((response) => {
      if (response.error) {
        return Promise.reject(response);
      } else {
        dispatch(retrievePaymentIntentSuccess(response.paymentIntent));
        return response;
      }
    })
    .catch((err) => {
      // Unwrap Stripe error.
      const e = err.error || storableError(err);
      dispatch(retrievePaymentIntentError(e));

      // Log error
      const { code, doc_url, message, payment_intent } = err.error || {};
      const loggableError = err.error
        ? {
          code,
          message,
          doc_url,
          paymentIntentStatus: payment_intent
            ? payment_intent.status
            : 'no payment_intent included',
        }
        : e;
      logError(loggableError, 'stripe-retrieve-payment-intent-failed', {
        stripeMessage: loggableError.message,
      });
      throw err;
    });
};

export const updatePaymentIntent = (params) => async (dispatch) => {
  try {
    dispatch(updatePaymentIntentRequest());
    const response = await stripeUpdatePaymentIntent(params);
    dispatch(updatePaymentIntentSuccess(response.data));
  } catch (err) {
    // Unwrap Stripe error.
    const e = err.error || storableError(err);
    dispatch(updatePaymentIntentError(e));

    // Log error
    const { code, doc_url, message, payment_intent } = err.error || {};
    const loggableError = err.error
      ? {
        code,
        message,
        doc_url,
        paymentIntentStatus: payment_intent
          ? payment_intent.status
          : 'no payment_intent included',
      }
      : e;
    logError(loggableError, 'stripe-retrieve-payment-intent-failed', {
      stripeMessage: loggableError.message,
    });
    throw err;
  }
};

// Redirect-based payment methods (PayPal, Klarna, bank redirects) are handed
// off by Stripe.js with `window.top.location.href = <pm-redirects URL>`. Stripe
// then waits 60s for a `pagehide` and, if the page is still alive, resolves the
// confirm with `{ code: 'redirect_error', message: 'Failed to redirect to …' }`
// while the PaymentIntent stays in `requires_action` — the transaction later
// dies in payment-expired.
//
// Sentry VANSITE-FRONTEND-4KG/4HW/4HQ show three to four such confirms per
// checkout, all failing within the same second: the customer tapped Pay
// repeatedly while the pre-confirm chain (elements.submit + update-payment-
// intent + order initiation) was still running, so several confirms raced and
// each started its own top-level navigation. None of them completed — had one
// won, its `pagehide` would have cleared every pending timer.
//
// StripePaymentForm guards the button, but any second entry point (a remount, a
// second tab, a redirected resubmit) can still double-confirm, so collapse
// concurrent confirms of the same client secret onto one in-flight promise.
const confirmsInFlight = new Map();

export const confirmStripePaymentIntent = (params) => (dispatch) => {
  const clientSecret = params.stripePaymentIntentClientSecret;
  if (!clientSecret) {
    return runConfirmStripePaymentIntent(params, dispatch);
  }

  const pending = confirmsInFlight.get(clientSecret);
  if (pending) {
    return pending;
  }

  const promise = runConfirmStripePaymentIntent(params, dispatch);
  confirmsInFlight.set(clientSecret, promise);
  const release = () => {
    if (confirmsInFlight.get(clientSecret) === promise) {
      confirmsInFlight.delete(clientSecret);
    }
  };
  promise.then(release, release);
  return promise;
};

const runConfirmStripePaymentIntent = async (params, dispatch) => {
  // It's required to use the same instance of Stripe as where the card has been created
  // so that's why Stripe needs to be passed here and we can't create a new instance.
  const {
    orderId,
    message,
    paymentFlowType,
    saveAfterOnetimePayment,
    stripe,
    paymentParams,
    stripePaymentIntentClientSecret,
  } = params;
  const { billing_details, payment_method } = paymentParams;

  dispatch(confirmPaymentRequest());

  // `typeof null === 'object'`, so a missing Payment Element used to be passed
  // on as `elements: null` and made stripe.confirmPayment throw (Sentry
  // VANSITE-FRONTEND-4H6). The Payment Element unmounts as soon as the form
  // switches to its error state, so a retry click can land here with nothing to
  // confirm. Fail with a storable error instead of crashing — the checkout then
  // renders its standard payment error.
  if (payment_method == null) {
    const missingPaymentMethod = new Error('confirm-payment-missing-payment-method');
    const e = storableError(missingPaymentMethod);
    dispatch(confirmStripePaymentError(e));
    logError(missingPaymentMethod, 'stripe-confirm-payment-missing-payment-method', {
      transactionId: orderId?.uuid || null,
      paymentFlowType,
      stripePaymentMethod: params.stripePaymentMethod || null,
    });
    throw e;
  }

  // Store message in sessionStorage to avoid URL length limits
  if (typeof window !== 'undefined' && window.sessionStorage) {
    try {
      const paypalReturnData = {};
      if (message) paypalReturnData.message = message;
      if (saveAfterOnetimePayment) paypalReturnData.saveAfterOnetimePayment = saveAfterOnetimePayment;
      if (Object.keys(paypalReturnData).length > 0) {
        window.sessionStorage.setItem(
          `paypal_return_${orderId.uuid}`,
          JSON.stringify(paypalReturnData),
        );
      }
    } catch (e) {
      // sessionStorage unavailable — message will fall back to URL params
    }
  }

  const searchParams = new URLSearchParams();
  searchParams.set('orderId', orderId.uuid);
  searchParams.set('paymentFlowType', paymentFlowType);
  if (saveAfterOnetimePayment) searchParams.set('saveAfterOnetimePayment', saveAfterOnetimePayment);

  const createReturnUrl = () => {
    const href = window.location.href;
    if (isCordova()) {
      searchParams.set('returnUrl', href);
      return `${cordovaApiBaseUrl()}/webhooks/paypal?${searchParams.toString()}`;
    }
    // Web: use URL API to properly handle existing query params
    const url = new URL(href);
    for (const [key, value] of searchParams) {
      url.searchParams.set(key, value);
    }
    return url.toString();
  };

  try {
    const response = await stripe.confirmPayment({
      clientSecret: stripePaymentIntentClientSecret,
      ...(typeof payment_method === 'object' ? { elements: payment_method } : {}),
      confirmParams: {
        return_url: createReturnUrl(),
        ...(typeof payment_method === 'string' ? { payment_method: payment_method } : {}),
        ...(typeof payment_method === 'object' ? { payment_method_data: { billing_details } } : {}),
      },
      redirect: 'if_required',
    });
    // stripe.confirmPayment resolves with `{ error }` on failure instead of
    // throwing — throw it ourselves so the catch below sees the real Stripe
    // error instead of a TypeError from reading paymentIntent.payment_method.
    if (response.error) {
      const wrapped = new Error(response.error.message);
      wrapped.error = response.error;
      throw wrapped;
    }
    dispatch(confirmStripePaymentSuccess(response.paymentIntent));
    return { paymentMethodId: response.paymentIntent.payment_method };
  } catch (err) {
    // A failed hand-off to the payment provider is not a declined payment: the
    // PaymentIntent is fine and still actionable, so it gets its own error code
    // (own Sentry issue, own customer-facing message with an intact retry)
    // instead of being lumped in with card failures.
    const isRedirectError = err.error?.code === 'redirect_error';
    const redirectUrl = err.error?.payment_intent?.next_action?.redirect_to_url?.url || null;

    // The hand-off URL carries a single-use PayPal nonce and must not reach
    // Sentry. It hides in three places: Stripe's raw message ("Failed to
    // redirect to https://pm-redirects.stripe.com/…/pa_nonce_…"), which
    // util/log.js turns into the Sentry exception message and the stripeMessage
    // extra, and `payment_intent.next_action`, which gets JSON-dumped whenever
    // the dispatched error is logged again downstream. Strip both here so no
    // caller has to remember to.
    const redactedRedirectMessage = 'Failed to redirect to the payment provider (URL redacted)';
    const sanitizeRedirectError = () => {
      const { message: rawMessage, payment_intent: intent, ...restOfError } = err.error;
      const { next_action: nextAction, ...restOfIntent } = intent || {};
      return {
        ...restOfError,
        message: redactedRedirectMessage,
        ...(intent ? { payment_intent: restOfIntent } : {}),
      };
    };

    // Unwrap Stripe error.
    const e = isRedirectError ? sanitizeRedirectError() : err.error || storableError(err);
    dispatch(confirmStripePaymentError(e));

    // Log error
    const containsPaymentIntent = err.error && err.error.payment_intent;
    const { code, doc_url, message, payment_intent } = containsPaymentIntent ? err.error : {};
    const loggableError = containsPaymentIntent
      ? {
        code,
        message: isRedirectError ? redactedRedirectMessage : message,
        doc_url,
        paymentIntentStatus: payment_intent.status,
      }
      : e;
    const logCode = isRedirectError
      ? 'stripe-confirm-redirect-failed'
      : 'stripe-handle-card-payment-failed';
    logError(loggableError, logCode, {
      stripeMessage: loggableError.message,
      // The URL itself carries a single-use PayPal nonce — record only whether
      // a resumable next_action was attached.
      hasRedirectUrl: !!redirectUrl,
      // Correlates the Sentry event with the transaction that ends up in
      // payment-expired when the confirm keeps failing.
      transactionId: orderId?.uuid || null,
      paymentFlowType,
      stripePaymentMethod: params.stripePaymentMethod || null,
      stripeErrorCode: err.error?.code || null,
      stripeDeclineCode: err.error?.decline_code || null,
      stripeErrorType: err.error?.type || null,
      paymentIntentStatus: err.error?.payment_intent?.status || null,
    });
    throw e;
  }
};

/**
 * @param {object} params
 * @param {Array<string>} [params.paymentMethodTypes] - Stripe payment method type
 * @param {string} [params.paymentMethodId] - Stripe payment method id
 * @param {string} params.stripeCustomerId - Stripe customer id
 */
export const createStripeSetupIntent =
  ({ paymentMethodId = undefined, stripeCustomerId = undefined }) =>
    async (dispatch) => {
      dispatch(setupIntentRequest());

      try {
        const response = await stripeSetupIntent({
          setupIntent: {
            customer: stripeCustomerId ? stripeCustomerId : undefined,
            payment_method_types: config.stripe.savablePaymentMethods,
            payment_method: paymentMethodId ? paymentMethodId : undefined,
          },
        });
        const setupIntent = response.data;
        dispatch(setupIntentSuccess(setupIntent));
        return setupIntent;
      } catch (e) {
        dispatch(setupIntentError(storableError(e)));
        logError(storableError(e), 'create-setup-intent-failed');
        return { createStripeSetupIntentSuccess: false };
      }
    };

export const confirmStripePaymentElementsSetupIntent =
  ({
    stripe,
    paymentElement,
    setupIntentClientSecret,
    billingDetails,
    paymentMethodType,
    paymentMethodId,
  }) =>
    async (dispatch) => {
      dispatch(handleStripeSetupRequest());
      try {
        if (paymentMethodType !== 'paypal') {
          paymentElement.update({
            mode: 'setup',
          });
          // submit() must be awaited and checked before confirmSetup — see
          // StripePaymentForm.handleSubmit for the wallet/user-activation details.
          const submitResult = (await paymentElement.submit()) || {};
          if (submitResult.error) {
            return Promise.reject(submitResult);
          }
          const response = await stripe.confirmSetup({
            elements: paymentElement,
            clientSecret: setupIntentClientSecret,
            confirmParams: {
              payment_method_data: {
                billing_details: billingDetails,
              },
            },
            redirect: 'if_required',
          });
          if (response.error) {
            return Promise.reject(response);
          } else {
            dispatch(handleStripeSetupSuccess(response));
            return response;
          }
        } else {
          const response = await stripe.retrieveSetupIntent(setupIntentClientSecret);
          if (response.error) {
            return Promise.reject(response);
          } else {
            dispatch(handleStripeSetupSuccess(response));
            return response;
          }
        }
      } catch (err) {
        const e = err.error || storableError(err);
        dispatch(handleStripeSetupError(e));

        // Log error
        const containsSetupIntent = err.error && err.error.setup_intent;
        const { code, doc_url, message, setup_intent } = containsSetupIntent ? err.error : {};
        const loggableError = containsSetupIntent
          ? {
            code,
            message,
            doc_url,
            paymentIntentStatus: setup_intent.status,
          }
          : e;
        logError(loggableError, `stripe-handle-${paymentMethodType}-setup-failed`, {
          stripeMessage: loggableError.message,
        });
        throw e;
      }
    };

/**
 * @param {object} params
 * @param {import('@stripe/stripe-js').Stripe} params.stripe Stripe instance
 * @param {import('@stripe/react-stripe-js').Elements} params.elements PaymentElement instance
 * @param {string} params.setupIntentClientSecret Stripe setup intent client secret
 * @param {string} [params.paymentMethodId] Stripe payment method id
 */
export const confirmStripeSetupIntent =
  ({ stripe, elements, setupIntentClientSecret, paymentMethodId = undefined, returnUrl }) =>
    async (dispatch) => {
      dispatch(handleStripeSetupRequest());

      try {
        const response = await stripe.confirmSetup({
          // @ts-ignore
          elements,
          clientSecret: setupIntentClientSecret,
          // @ts-ignore
          confirmParams: {
            payment_method: paymentMethodId,
            return_url: returnUrl,
          },
          // @ts-ignore
          redirect: 'if_required',
        });

        if (response.error) {
          throw response;
        } else {
          dispatch(handleStripeSetupSuccess(response));
          return response;
        }
      } catch (err) {
        // Unwrap Stripe error.
        const e = err.error || storableError(err);
        dispatch(handleStripeSetupError(e));

        // Log error
        const containsSetupIntent = err.error && err.error.setup_intent;
        const { code, doc_url, message, setup_intent } = containsSetupIntent ? err.error : {};
        const loggableError = containsSetupIntent
          ? {
            code,
            message,
            doc_url,
            paymentIntentStatus: setup_intent.status,
          }
          : e;
        logError(loggableError, 'stripe-handle-setup-failed', {
          stripeMessage: loggableError.message,
        });
        throw e;
      }
    };

export const updateStripeCustomer = (params) => async (dispatch) => {
  try {
    dispatch(updateStripeCustomerRequest());
    const response = await stripeUpdateCustomer(params);
    dispatch(updateStripeCustomerSuccess(response.data));
    dispatch(fetchCurrentUser());
  } catch (err) {
    logError(err, 'stripe-update-customer-failed');
    throw err;
  }
};